// src/app/api/adminApi.ts

import { baseApi } from "./baseApi";
import type { StudentTrack, StudentStatus } from "./authApi";
import type { PayStatus, EnrolStatus, Quarter } from "./parentApi";
import type { BillingPeriod } from "./enrolmentApi";

/* ─── SHARED RESPONSE WRAPPERS ───────────────────────────────────── */

interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface PagedListResponse<T> {
  success: boolean;
  count:   number;
  total:   number;
  page:    number;
  pages:   number;
  data:    T[];
}

/* ─── STUDENT ────────────────────────────────────────────────────── */

export interface AdminStudent {
  id:            string;
  firstName:     string;
  lastName:      string;
  age:           number;
  dateOfBirth:   string;
  track:         StudentTrack;
  studentStatus: StudentStatus;
  email:         string | null;
  avatar:        string | null;
  parent: {
    id:        string;
    firstName: string;
    lastName:  string;
    email:     string;
  } | null;
  activeEnrolment: {
    id:               string;
    quarter:          Quarter;
    academicYear:     string;
    status:           EnrolStatus;
    paymentStatus:    PayStatus;
    billingPeriod:    BillingPeriod | null;
    billingLabel:     string | null;
    progressPercent:  number;
    currentWeek:      number;
    feeAmount:        number;
    nextPaymentLabel: string | null;
  } | null;
  createdAt: string;
}

export interface AdminStudentDetail extends AdminStudent {
  enrolments: any[];
  payments:   any[];
}

export interface GetAdminStudentsParams {
  track?:         StudentTrack;
  studentStatus?: StudentStatus;
  search?:        string;
  page?:          number;
  limit?:         number;
}

/* ─── PAYMENT ────────────────────────────────────────────────────── */

export interface AdminPayment {
  id:               string;
  description:      string;
  type:             string;
  amount:           number;
  amountFormatted:  string;
  currency:         string;
  status:           PayStatus;
  method:           string | null;
  reference:        string | null;
  authorizationUrl: string | null;
  gatewayResponse:  string | null;
  dueDate:          string | null;
  paidAt:           string | null;
  receiptUrl:       string | null;
  notes:            string | null;
  child:     { id: string; firstName: string; lastName: string } | null;
  parent:    { id: string; firstName: string; lastName: string; email: string } | null;
  enrolment: string | null;
  createdAt: string;
  updatedAt: string;
  /** The 14-day money-back guarantee */
  refundRequest?: {
    status: "requested" | "approved" | "declined";
    requestedAt: string;
    reason: string | null;
    decidedAt: string | null;
    decidedByName: string | null;
    note: string | null;
  } | null;
}

export interface GetAdminPaymentsParams {
  parentId?: string;
  childId?:  string;
  status?:   PayStatus;
  refund?:   "requested" | "approved" | "declined";
  method?:   string;
  from?:     string;
  to?:       string;
  page?:     number;
  limit?:    number;
}

export interface AdminPaymentsSummary {
  totalPaid:    number;
  totalPending: number;
  count:        number;
}

/* ─── DASHBOARD ──────────────────────────────────────────────────── */

export interface AdminDashboardData {
  stats: {
    totalStudents:         number;
    activeEnrolments:      number;
    totalRevenue:          number;
    totalRevenueFormatted: string;
  };
  trackDistribution: {
    foundation_builders: number;
    explorers:           number;
    innovators:          number;
    unassigned:          number;
  };
  recentEnrolments: Array<{
    id:           string;
    child:        { _id: string; firstName: string; lastName: string; age: number; track: string } | null;
    quarter:      string;
    academicYear: string;
    status:       EnrolStatus;
    paymentStatus: PayStatus;
    feeAmount:    number;
    billingPeriod: BillingPeriod | null;
    billingLabel:  string | null;
    createdAt:    string;
  }>;
}

/* ─── API SLICE ──────────────────────────────────────────────────── */

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    /* ── Dashboard ── */
    adminGetDashboard: builder.query<SingleResponse<AdminDashboardData>, void>({
      query: () => "/admin/dashboard",
      providesTags: [{ type: "AdminDashboard" as const, id: "STATS" }],
    }),

    /* ── Students ── */
    adminGetStudents: builder.query<PagedListResponse<AdminStudent>, GetAdminStudentsParams>({
      query: (params = {}) => ({ url: "/admin/students", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Students" as const, id })),
              { type: "Students" as const, id: "LIST" },
            ]
          : [{ type: "Students" as const, id: "LIST" }],
    }),

    adminGetStudent: builder.query<SingleResponse<AdminStudentDetail>, string>({
      query: (id) => `/admin/students/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Students" as const, id }],
    }),

    /* ── Payments ── */
    adminGetPayments: builder.query<
      PagedListResponse<AdminPayment> & { summary: AdminPaymentsSummary },
      GetAdminPaymentsParams
    >({
      query: (params = {}) => ({ url: "/payments/admin", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminPayments" as const, id })),
              { type: "AdminPayments" as const, id: "LIST" },
            ]
          : [{ type: "AdminPayments" as const, id: "LIST" }],
    }),

    adminGetPayment: builder.query<SingleResponse<AdminPayment>, string>({
      query: (id) => `/payments/admin/${id}`,
      providesTags: (_r, _e, id) => [{ type: "AdminPayments" as const, id }],
    }),

    adminConfirmPayment: builder.mutation<
      SingleResponse<any>,
      { enrolmentId: string; reference: string; paidAt?: string; notes?: string }
    >({
      query: (body) => ({ url: "/payments/admin/confirm", method: "POST", body }),
      invalidatesTags: [
        { type: "AdminPayments" as const, id: "LIST" },
        { type: "Enrolments"    as const, id: "ADMIN_LIST" },
        { type: "AdminDashboard" as const, id: "STATS" },
      ],
    }),

    adminDecideRefund: builder.mutation<
      SingleResponse<AdminPayment> & { message?: string },
      { id: string; decision: "approve" | "decline"; note?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/payments/admin/${id}/refund-request`, method: "POST", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminPayments"  as const, id },
        { type: "AdminPayments"  as const, id: "LIST" },
        { type: "AdminDashboard" as const, id: "STATS" },
      ],
    }),

    adminRefundPayment: builder.mutation<
      SingleResponse<any>,
      { id: string; amountNaira?: number; reason?: string }
    >({
      query: ({ id, ...body }) => ({
        url:    `/payments/admin/${id}/refund`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminPayments"  as const, id },
        { type: "AdminPayments"  as const, id: "LIST" },
        { type: "AdminDashboard" as const, id: "STATS" },
      ],
    }),
  }),
});

export const {
  useAdminGetDashboardQuery,
  useAdminGetStudentsQuery,
  useAdminGetStudentQuery,
  useAdminGetPaymentsQuery,
  useAdminGetPaymentQuery,
  useAdminConfirmPaymentMutation,
  useAdminRefundPaymentMutation,
  useAdminDecideRefundMutation,
} = adminApi;